
// Using the Place interface from the component
interface Place {
  id?: number;
  name: string;
  latitude: string | number;
  longitude: string | number;
}

// Interface for geolocation position
export interface Position {
  coords: {
    latitude: number;
    longitude: number;
  };
}

/**
 * Calculate the distance between two geographic coordinates in kilometers
 * using the Haversine formula
 * 
 * @param lat1 - Latitude of first point in decimal degrees
 * @param lon1 - Longitude of first point in decimal degrees
 * @param lat2 - Latitude of second point in decimal degrees
 * @param lon2 - Longitude of second point in decimal degrees
 * @returns Distance in kilometers
 */
export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * 
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return distance;
};

/**
 * Convert decimal degrees to radians
 */
const toRad = (value: number): number => {
  return value * Math.PI / 180;
};

/**
 * Find the closest place to the user's current position
 * 
 * @param userPosition - User's current geolocation position
 * @param places - Array of places to check against
 * @returns The closest place or null if no places provided
 */
export const findClosestPlace = (
  userPosition: Position,
  places: Place[]
): Place | null => {
  if (!userPosition?.coords || !places || places.length === 0) {
    return null;
  }

  const { latitude: userLat, longitude: userLon } = userPosition.coords;
  
  let closestPlace: Place | null = null;
  let shortestDistance = Infinity;
  
  places.forEach(place => {
    if (place.latitude && place.longitude) {      const distance = calculateDistance(
        userLat,
        userLon,
        typeof place.latitude === 'string' ? parseFloat(place.latitude) : place.latitude,
        typeof place.longitude === 'string' ? parseFloat(place.longitude) : place.longitude
      );
      
      if (distance < shortestDistance) {
        shortestDistance = distance;
        closestPlace = place;
      }
    }
  });
  
  return closestPlace;
};

/**
 * Positions the "You Are Here!" card above the venue element that corresponds to the closest place
 * 
 * @param cardElement - The DOM element of the "You Are Here!" card
 * @param closestPlace - The place that is closest to the user's location
 * @returns A boolean indicating whether positioning was successful
 */
export const positionCardAboveClosestVenue = (
  cardElement: HTMLElement | null,
  closestPlace: Place | null
): boolean => {
  if (!cardElement || !closestPlace) {
    return false;
  }
  
  // Try multiple approaches to find the venue element
  let venueElement: HTMLElement | null = null;
  
  // 1. Try to find by ID first (various formats)
  const possibleIds = [
    `venue-${closestPlace.id}`,
    `place-${closestPlace.id}`,
    `venue-${closestPlace.name.toLowerCase().replace(/\s+/g, '-')}`,
    closestPlace.name.toLowerCase().replace(/\s+/g, '-')
  ];
  
  for (const id of possibleIds) {
    const element = document.getElementById(id);
    if (element) {
      venueElement = element;
      break;
    }
  }
  
  // 2. Try to find by looking for <summary> elements containing the place name
  if (!venueElement) {
    // Look for <details> or <summary> elements that include the place name
    const summaryElements = document.querySelectorAll('summary');
    for (let i = 0; i < summaryElements.length; i++) {
      const element = summaryElements[i] as HTMLElement;
      // Use more precise text matching - consider only the direct text content
      const elementText = element.childNodes[0]?.nodeValue?.trim() || element.innerText.trim();
      
      if (elementText === closestPlace.name || 
          elementText.includes(closestPlace.name) ||
          element.textContent?.includes(closestPlace.name)) {
        // Get the parent details element
        venueElement = element.closest('details') || element;
        break;
      }
    }
  }
  
  // 3. If we still can't find it by summary, look more broadly for any elements with the venue name
  if (!venueElement) {
    // In the Events By Place section, look for elements containing the venue name
    const byPlaceSection = document.getElementById('by-place')?.parentElement || document;
    const allPossibleVenueElements = byPlaceSection.querySelectorAll('*');
    
    for (let i = 0; i < allPossibleVenueElements.length; i++) {
      const element = allPossibleVenueElements[i] as HTMLElement;
      if (element.textContent?.trim() === closestPlace.name ||
          (element.tagName !== 'DETAILS' && 
           element.textContent?.includes(closestPlace.name) && 
           !element.querySelector('*')?.textContent?.includes(closestPlace.name))) {
        // Found an element with the venue name that doesn't have children with the venue name
        venueElement = element.closest('details') || element;
        break;
      }
    }
  }
  
  if (!venueElement) {
    console.warn(`Venue element for ${closestPlace.name} (ID: ${closestPlace.id}) not found in DOM`);
    return false;
  }
    // Position the card above the venue element
  try {
    //console.log(`Found venue element for ${closestPlace.name}:`, venueElement);
    const parentElement = venueElement.parentElement;
    
    if (parentElement) {
      // Set a data attribute for debugging
      cardElement.dataset.positionedAbove = closestPlace.name;
      
      // Insert the card before the venue element
      parentElement.insertBefore(cardElement, venueElement);
      
      // Force a reflow to ensure the DOM update takes effect before adding the positioned class
      void cardElement.offsetWidth;
      
      // Add positioned class for animation
      cardElement.classList.add('positioned');
      
      //console.log(`Successfully positioned card above ${closestPlace.name}`);
      return true;
    } else {
      console.warn(`Parent element not found for ${closestPlace.name} venue element`);
    }
  } catch (error) {
    console.error('Error positioning card:', error);
  }
  
  return false;
};

/**
 * Reset the card's position to its default state
 * 
 * @param cardElement - The DOM element of the "You Are Here!" card
 * @returns A boolean indicating whether the reset was successful
 */
export const resetCardPosition = (
  cardElement: HTMLElement | null
): boolean => {
  if (!cardElement) {
    return false;
  }
  
  try {
    // First, check if the card is actually in the DOM
    // This prevents most "removeChild" errors when the node is already gone
    const isInDOM = document.body.contains(cardElement);
    if (!isInDOM) {
      //console.log('Card element is not in the DOM, no need to reset position');
      return false;
    }
    
    // Remove the positioned class to hide it with CSS transitions
    cardElement.classList.remove('positioned');
    
    // Clear the data attribute
    if (cardElement.dataset.positionedAbove) {
      delete cardElement.dataset.positionedAbove;
    }
    
    // Safely remove the card from its current parent if it has one
    const safelyRemoveCard = () => {
      try {
        // Double check that the element is still in the DOM at the time of removal
        // This avoids race conditions with React's own cleanup
        if (!document.body.contains(cardElement)) {
          //console.log('Card was already removed from DOM, skipping manual removal');
          return;
        }
        
        // Only attempt to remove if there's a parent node and it still contains the card
        if (cardElement?.parentNode?.contains?.(cardElement)) {
          try {
            // Store reference to parent before removal for verification
            const parent = cardElement.parentNode;
            
            // Remove the original from its current parent
            parent.removeChild(cardElement);
            
            // No need to reinsert the card when we're cleaning up
            // Let React handle the re-rendering if needed
            
            //console.log('Successfully reset card position');
            return;
          } catch (removeError) {
            console.warn('Could not remove card:', removeError);
            // If direct removal fails, just hide it with CSS instead
            cardElement.style.display = 'none';
            cardElement.style.opacity = '0';
            return;
          }
        } else {
          //console.log('Parent node no longer contains card or is invalid');
        }
      } catch (err) {
        //console.log('Safe removal fallback: Card will be unmounted by React', err);
      }
    };
      
    // After the transition completes, reset its position in the DOM
    // Use requestAnimationFrame to ensure we're not interfering with React's own DOM operations
    requestAnimationFrame(() => {
      // Increase timeout to ensure CSS transitions complete first
      setTimeout(safelyRemoveCard, 350); // Match this to your CSS transition duration plus a small buffer
    });
    
    return true;
  } catch (error) {
    console.error('Error resetting card position:', error);
    return false;
  }
};

/**
 * Initialize the dynamic positioning of the "You Are Here!" card
 * Can be called when component mounts or when geolocation/places data updates
 * 
 * @param cardRef - React ref object containing the card element
 * @param userPosition - User's current geolocation position
 * @param places - Array of places to check against
 * @param retryOptions - Configuration for retry attempts (optional)
 * @returns The closest place found (or null) and whether positioning was successful
 */
export const initializeDynamicPositioning = (
  cardRef: React.RefObject<HTMLDivElement>,
  userPosition: Position | null,
  places: Place[],
  retryOptions?: { 
    maxRetries?: number, 
    retryDelay?: number,
    onRetry?: (attempt: number, closestPlace: Place) => void 
  }
): { closestPlace: Place | null, success: boolean } => {
  // If we don't have position or places data, we can't position dynamically
  if (!userPosition || !places || places.length === 0) {
    return { closestPlace: null, success: false };
  }
  
  // Find the closest place to the user
  const closestPlace = findClosestPlace(userPosition, places);
  
  if (!closestPlace || !cardRef.current) {
    return { closestPlace, success: false };
  }
  
  // Set up retry configuration with defaults
  const maxRetries = retryOptions?.maxRetries || 3;
  const retryDelay = retryOptions?.retryDelay || 500;
  const onRetry = retryOptions?.onRetry || ((attempt, place) => {
    //console.log(`Retry attempt ${attempt} for positioning card above ${place.name}`);
  });
  
  // Try to position the card with retries
  let success = positionCardAboveClosestVenue(cardRef.current, closestPlace);
  
  // If unsuccessful on first try, set up retries
  if (!success && maxRetries > 0) {
    let retryCount = 0;
    
    const attemptRetry = () => {
      if (retryCount < maxRetries) {
        retryCount++;
        onRetry(retryCount, closestPlace);
        
        setTimeout(() => {
          if (cardRef.current) {
            const retrySuccess = positionCardAboveClosestVenue(cardRef.current, closestPlace);
            if (retrySuccess) {
              success = true;
              //console.log(`Successfully positioned card on retry attempt ${retryCount}`);
            } else if (retryCount < maxRetries) {
              attemptRetry();
            }
          }
        }, retryDelay);
      }
    };
    
    attemptRetry();
  }
  
  return { closestPlace, success };
};